import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

// Material UI
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ViewIcon from '@material-ui/icons/Visibility';

// Component
import { FormInput } from '../../_core/_ui/forms.component';
import { EnhancedTable, IEnhancedTableColumn, IEnhancedTableRow } from '../../_core/_ui/table.components';
import { toScore } from '../../../lib/helpers/conversion.helpers';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { Spacer } from '../../_core/_ui/structure.components';
import SendIcon from '@material-ui/icons/Send';
import { buildSendEmails, getCountHasUnsentType } from '../../../lib/services/email-log.service';
import { useCompanyHasReportCards, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { Sport, SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SPORT_TERMINOLOGY } from '../../../lib/constants';
import { Fade, Modal, useMediaQuery, useTheme } from '@material-ui/core';
import { CheckCircle, WarningOutlined } from '@material-ui/icons';
import ConcludePlayerComponent from '../ConcludePlayer/conclude-player.component';
import { RsvpSummaryCell } from '../../Attendance/rsvp-summary-cell.component';
import { validateAttendanceRatio } from '../../Attendance/attendance.helpers';
import { getPlayerStatus } from '../../../lib/utils/player.utils';
import { IConcludePlayerOptions } from '../types/conclude-player-options.types';
import { getPlayerName } from '../../../lib/utils/player-name.service';
import { MultiActionReportCardButton } from './multi-action-report-card-button.component';
import { ContactReachIcon } from '../../PlayerContact/contact-reach-icon.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { ReportFreshnessNotice } from '../report-freshness-notice.component';
import { getMessages } from '../report.messages';
import { SearchField } from '../../_core/_ui/search-field.component';
import { useDebouncedValue } from '../../../lib/hooks/useDebouncedValue';
import { buildPlayerNameMatcher } from '../../../lib/helpers/player-search.helpers';
import { ReportCardStatusChip } from '../../ReportCardAccess/report-card-status-chip.component';
import { ReportCardStatusFilter } from '../../ReportCardAccess/report-card-access.types';
import { RankingReportCardFilter } from './ranking-report-card-filter.component';
import { ConcludedHiddenNotice } from './concluded-hidden-notice.component';
import {
  countReportCardStatusFilters,
  filterByGoalie,
  filterRankingPlayerAssessments,
  GoalieFilter,
} from './player-ranking-report.utils';

interface IPlayerRankingReport {
  loading: boolean;
  fetchError: boolean;
  playerAssessments: IPlayerAssessment[];
  exportReports: ClickHandler;
  selected: IPlayerAssessment[];
  setSelected: Function;
  level: string;
  setLevel: Function;
  assignLevel: ClickHandler;
  ageGroup?: IAgeGroup;
  team: string;
  setTeam: Function;
  assignTeam: ClickHandler;
  sendNotifications: boolean;
  setSendNotifications: Function;
  concludePlayers: ClickHandler;
  concludeEmailTemplate: string;
  setConcludeEmailTemplate: Function;
  concludeEmailTemplateOptions: IConcludePlayerOptions[];
  goalieFilter: GoalieFilter;
}

export const PlayerRankingReport: React.FC<IPlayerRankingReport> = ({
  fetchError,
  loading,
  playerAssessments,
  exportReports,
  selected,
  setSelected,
  level,
  setLevel,
  assignLevel,
  team,
  setTeam,
  assignTeam,
  ageGroup,
  sendNotifications,
  setSendNotifications,
  concludePlayers,
  concludeEmailTemplate,
  setConcludeEmailTemplate,
  concludeEmailTemplateOptions,
  goalieFilter
}) => {
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const hasReportCards = useCompanyHasReportCards()
  const termLookup = useCompanySportTerminology()
  const messages = getMessages(termLookup);
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const athletesTerm = pluralTerm(athleteTerm).toLowerCase();

  const playersWithLevel: any = _.filter(playerAssessments, playerAssessment => playerAssessment.level);
  const levelPlayerAssessmentIds = _.map(playersWithLevel, '_id');
  const playersWithTeam: any = _.filter(playerAssessments, playerAssessment => playerAssessment.team);
  const teamPlayerAssessmentIds = _.map(playersWithTeam, '_id');

  const { count: countLevel, loading: loadingLevelEmail, error: levelCountError, refetch: refetchLevelEmail } = getCountHasUnsentType('LEVEL_ASSIGNMENT', ageGroup?._id, levelPlayerAssessmentIds);
  const [sendingLevel, setSendingLevel] = useState(false);
  const { count: countTeam, loading: loadingTeamEmail, error: teamCountError, refetch: refetchTeamEmail } = getCountHasUnsentType('TEAM_ASSIGNMENT', ageGroup?._id, teamPlayerAssessmentIds);
  const [sendingTeam, setSendingTeam] = useState(false);
  const { sendEmails } = buildSendEmails();

  const history = useHistory();

  const handleSendLevel = () => {
    setSendingLevel(true);

    return sendEmails('LEVEL_ASSIGNMENT', ageGroup?._id)
      .then(() => refetchLevelEmail())
      .then(() => setSendingLevel(false));
  }

  const handleSendTeam = () => {
    setSendingTeam(true);

    return sendEmails('TEAM_ASSIGNMENT', ageGroup?._id)
      .then(() => refetchTeamEmail())
      .then(() => setSendingTeam(false));
  }

  const positions = useMemo(() => _.uniq(playerAssessments.map(playerAssessment => playerAssessment.position?.name).filter(position => !!position && position.toLowerCase() !== 'goalie')), [playerAssessments]);
  const levels = useMemo(() => _.uniq(playerAssessments.map(playerAssessment => playerAssessment.level).filter(level => !!level)), [playerAssessments]);
  const teams = useMemo(() => _.uniq(playerAssessments.map(playerAssessment => playerAssessment.team).filter(team => !!team)), [playerAssessments]);

  const [positionFilter, setPositionFilter] = useState<string[]>([]);
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [teamFilter, setTeamFilter] = useState<string[]>([]);
  const [includeConcludedFilter, setIncludeConcludedFilter] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<ReportCardStatusFilter[]>([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  function togglePositionFilter(position: string) {
    if (positionFilter.indexOf(position) > -1) {
      // Remove it.
      setPositionFilter([...positionFilter].filter(p => position !== p));
    } else {
      setPositionFilter([...positionFilter, position]);
    }
  }

  function toggleLevelFilter(level: string) {
    if (levelFilter.indexOf(level) > -1) {
      // Remove it.
      setLevelFilter([...levelFilter].filter(p => level !== p));
    } else {
      setLevelFilter([...levelFilter, level]);
    }
  }

  function toggleTeamFilter(team: string) {
    if (teamFilter.indexOf(team) > -1) {
      // Remove it.
      setTeamFilter([...teamFilter].filter(p => team !== p));
    } else {
      setTeamFilter([...teamFilter, team]);
    }
  }

  function toggleIncludeConcludedFilter(includeConcluded: boolean) {
    setIncludeConcludedFilter(includeConcluded);
  }

  const toggleStatusFilter = useCallback((filter: ReportCardStatusFilter) => {
    setStatusFilter(current => (current.includes(filter) ? current.filter(f => f !== filter) : [...current, filter]));
  }, []);

  const showConcluded = useCallback(() => setIncludeConcludedFilter(true), []);

  const rosterPlayerAssessments = useMemo(() => filterByGoalie(playerAssessments, goalieFilter), [playerAssessments, goalieFilter]);
  const matchPlayerNames = useMemo(() => buildPlayerNameMatcher(rosterPlayerAssessments, 'player'), [rosterPlayerAssessments]);
  const reportCardStatusCounts = useMemo(() => countReportCardStatusFilters(rosterPlayerAssessments), [rosterPlayerAssessments]);
  const hasReportCardStatuses = useMemo(
    () => hasReportCards && rosterPlayerAssessments.some(playerAssessment => !!playerAssessment.reportCardAccess),
    [hasReportCards, rosterPlayerAssessments],
  );

  const { shown: filteredPlayerAssessments, hiddenConcludedCount } = useMemo(() => filterRankingPlayerAssessments(rosterPlayerAssessments, {
    positions: positionFilter,
    levels: levelFilter,
    teams: teamFilter,
    statusFilters: hasReportCardStatuses ? statusFilter : [],
    nameMatches: debouncedSearch ? new Set(matchPlayerNames(debouncedSearch)) : null,
    includeConcluded: includeConcludedFilter,
  }), [rosterPlayerAssessments, positionFilter, levelFilter, teamFilter, hasReportCardStatuses, statusFilter, debouncedSearch, matchPlayerNames, includeConcludedFilter]);
  const isNarrowed = !!debouncedSearch || (hasReportCardStatuses && statusFilter.length > 0);

  const rows = useMemo(() => {
    const nextRows: IEnhancedTableRow[] = [];
    _.each(filteredPlayerAssessments, (playerAssessment: IPlayerAssessment, index) => {
      let adjustedScore = '-';

      if (playerAssessment.adjustedScore && playerAssessment.adjustedScore !== '-') {
        adjustedScore = toScore(playerAssessment.adjustedScore || '-', 1);
      }

      const attendanceRatio = validateAttendanceRatio(
        playerAssessment.attendanceCount,
        playerAssessment.assessmentSessionCount,
        `PlayerRankingReport[${playerAssessment._id}]`,
      );
      const attendanceCellValue = attendanceRatio
        ? `${attendanceRatio.attended} / ${attendanceRatio.total}`
        : '—';

      const cells = [
        <TableCell key="name">{getPlayerName(playerAssessment)}</TableCell>,
        <TableCell key="position">{playerAssessment.position?.name}</TableCell>,
        <TableCell key="attendance">{attendanceCellValue}</TableCell>,
        <TableCell key="rsvp">
          <RsvpSummaryCell counts={(playerAssessment.attendanceCounts?.total || 0) > 0 ? playerAssessment.attendanceCounts : undefined} />
        </TableCell>,
        <TableCell key="overallRank">{playerAssessment.overallRank}</TableCell>,
        <TableCell key="positionRank">{playerAssessment.positionRank}</TableCell>,
        <TableCell key="adjusted">{adjustedScore}</TableCell>,
        <TableCell key="level">{playerAssessment.level}</TableCell>,
        <TableCell key="team">{playerAssessment.team}</TableCell>,
        <TableCell key="isConcluded">{playerAssessment.isConcluded && (<CheckCircle color="error"  />)}{playerAssessment.concludedAt ? ` (${dayjs(playerAssessment.concludedAt).format('MMM D YYYY')})` : '' }</TableCell>,
        <TableCell key="status">{getPlayerStatus(playerAssessment)}</TableCell>,
        hasReportCards && (
          <TableCell key="reportCard">
            <ReportCardStatusChip access={playerAssessment.reportCardAccess} />
          </TableCell>
        ),
        <TableCell key="edit" padding="checkbox">
          <Tooltip title={`View Score Breakdown (Bottom of ${termLookup[SportTerminology.ATHLETE]} Page)`}>
            <IconButton
              onClick={() => history.push(
                `/${pluralTerm(SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE].toLowerCase())}/${playerAssessment.player._id}/${playerAssessment._id}`,
                { back: history.location.pathname }
              )}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      ]

      if (hasReportCards) {
        cells.push(
          <MultiActionReportCardButton
            key="report-card-actions"
            playerAssessment={playerAssessment}
          />
        );
      }
      cells.push(
        <TableCell key="warnings" padding="checkbox">
          <Box display="flex" alignItems="center" justifyContent="center" style={{ padding: '12px', gap: '8px' }}>
            {!!playerAssessment?.unappliedActionsCount &&
              <Tooltip title={`Found ${playerAssessment.unappliedActionsCount} new scores`}>
                <WarningOutlined color="error" />
              </Tooltip>
            }
            <ContactReachIcon contactReach={playerAssessment.contactReach} />
          </Box>
        </TableCell>
      )

      nextRows.push({
        _id: playerAssessment._id,
        data: {...playerAssessment, adjusted: Number(playerAssessment.adjustedScore), rank: index + 1},
        cells: cells.filter(Boolean)
      });
    })

    return nextRows;
  }, [filteredPlayerAssessments, hasReportCards, history, termLookup]);

  const columns = useMemo(() => {
    const nextColumns = [
      { key: 'name', label: 'Name', path: 'player.lastName' },
      { key: 'position', label: 'Position', path: 'position.name' },
      { key: 'attendanceCount', label: 'Attended' },
      { key: 'rsvp', label: 'RSVP', disableSort: true, helper: 'Shown as attending/pending/not-attending' },
      { key: 'overallRank', label: 'Rank' },
      { key: 'positionRank', label: 'Position Rank' },
      { key: 'adjusted', label: 'UNBIAS™ Result' },
      { key: 'level', label: 'Level' },
      { key: 'team', label: 'Team' },
      { key: 'isConcluded', label: 'Concluded' },
      { key: 'status', label: 'Status', disableSort: true },
      hasReportCards && { key: 'reportCard', label: 'Report Card', disableSort: true },
      { key: 'edit', disableSort: true, width: '60px', disablePadding: true }
    ].filter(Boolean) as IEnhancedTableColumn[]

    if (hasReportCards) nextColumns.push({ key: 'report', disableSort: true, width: '60px', disablePadding: true })
    nextColumns.push({ key: 'warnings', disableSort: true, width: '80px', disablePadding: true })
    return nextColumns;
  }, [hasReportCards]);

  const buttonStyle = { minWidth: '130px', marginLeft: '16px' }

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 650,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };


  const [openModal, setOpenModal] = useState<boolean>(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  return (
    <>
      <Box mb={2} display="flex" alignItems="center">
        <ReportFreshnessNotice />
        <Spacer />
        <PrimaryButton onClick={exportReports}><IntlMsg {...messages.btnExportWithContacts} /></PrimaryButton>
      </Box>
      <Box mb={1} maxWidth={320}>
        <SearchField value={search} onChange={setSearch} placeholder="Search by name" />
      </Box>
      {
        positions.length > 0 && goalieFilter !== 'goalie' && (
        <Box mb={0} display="flex" alignItems="center">
          <Box><strong><IntlMsg {...messages.lblPositionFilter} /></strong></Box>
          {
            positions.map(position => {
              return (
                <Box key={position}>
                  <Checkbox
                    checked={positionFilter.indexOf(position) > -1}
                    onChange={(e) => togglePositionFilter(position)}
                  />
                  {position}
                </Box>
              )
            })
          }
        </Box>
        )
      }

      {
        levels.length > 0 && (
          <Box mb={0} display="flex" alignItems="center">
            <Box><strong><IntlMsg {...messages.lblLevelFilter} /></strong></Box>
            {
              levels.map(level => {
                return (
                  <Box key={level}>
                    <Checkbox
                      checked={levelFilter.indexOf(level) > -1}
                      onChange={(e) => toggleLevelFilter(level)}
                    />
                    {level}
                  </Box>
                )
              })
            }
          </Box>
        )
      }

      {
        teams.length > 0 && (
        <Box mb={0} display="flex" alignItems="center">
          <Box><strong><IntlMsg {...messages.lblTeamFilter} /></strong></Box>
          {
            teams.map(team => {
              return (
                <Box key={team}>
                  <Checkbox
                    checked={teamFilter.indexOf(team) > -1}
                    onChange={(e) => toggleTeamFilter(team)}
                  />
                  {team}
                </Box>
              )
            })
          }
        </Box>
        )
      }
      {hasReportCardStatuses && (
        <RankingReportCardFilter selected={statusFilter} counts={reportCardStatusCounts} onToggle={toggleStatusFilter} />
      )}
      <Box mb={2} display="flex" alignItems="center">
        <Box>
          <strong><IntlMsg {...messages.lblShowConcluded} /></strong>
          <Checkbox
            checked={includeConcludedFilter}
            onChange={() => toggleIncludeConcludedFilter(!includeConcludedFilter)}
          />
        </Box>
      </Box>
      {isNarrowed && hiddenConcludedCount > 0 && (
        <ConcludedHiddenNotice count={hiddenConcludedCount} onShowConcluded={showConcluded} />
      )}
      {(!!levelCountError || (countLevel !== null && countLevel > 0) || loadingLevelEmail) && (
        <Box display="flex" alignItems="center" mb={2}>
          {loadingLevelEmail && <Typography>Checking whether any {athletesTerm} need to receive a level notification...</Typography>}
          {!loadingLevelEmail && !levelCountError && countLevel !== null && countLevel > 0 && <Typography>Send level notifications to {countLevel} {athletesTerm} that have not received one</Typography>}
          {!loadingLevelEmail && !!levelCountError && (
            <Typography color="error">
              Couldn&apos;t check whether any {athletesTerm} need a level notification. Refresh to try again.
            </Typography>
          )}
          <Spacer />
          <PrimaryButton disabled={loadingLevelEmail || sendingLevel || !!levelCountError} endIcon={<SendIcon />} onClick={() => handleSendLevel()}>Send Level Emails</PrimaryButton>
        </Box>
      )}
      {(!!teamCountError || (countTeam !== null && countTeam > 0) || loadingTeamEmail) && (
        <Box display="flex" alignItems="center" mb={2}>
          {loadingTeamEmail && <Typography>Checking whether any {athletesTerm} need to receive a team notification...</Typography>}
          {!loadingTeamEmail && !teamCountError && countTeam !== null && countTeam > 0 && <Typography>Send team notifications to {countTeam} {athletesTerm} that have not received one</Typography>}
          {!loadingTeamEmail && !!teamCountError && (
            <Typography color="error">
              Couldn&apos;t check whether any {athletesTerm} need a team notification. Refresh to try again.
            </Typography>
          )}
          <Spacer />
          <PrimaryButton disabled={loadingTeamEmail || sendingTeam || !!teamCountError} endIcon={<SendIcon />} onClick={() => handleSendTeam()}>Send Team Emails</PrimaryButton>
        </Box>
      )}

      <EnhancedTable
        selected={selected}
        setSelected={setSelected}
        numCols={columns.length}
        rows={rows}
        columns={columns}
        defaultOrder="overallRank"
        entityName="players"
        showError={fetchError}
        showLoading={loading && rows.length === 0}
        checkboxTitle="Assign"
        title={termLookup[SportTerminology.ATHLETE] + " Rankings"}
        selectedAction={(
          <Box display="flex" flexWrap={isLarge ? undefined : "wrap"}>
            <Box display="flex" p={2} style={{ height: '72px'}}>
              <FormInput type="text" placeholder="Level Placement" onKeyUp={(e: any) => setLevel(e.currentTarget.value)} />
              <PrimaryButton
                disabled={!level || level.length === 0}
                style={buttonStyle}
                onClick={assignLevel}
              >Assign Level</PrimaryButton>
              <Box mr={4} />
              <FormInput type="text" placeholder="Team Placement" onKeyUp={(e: any) => setTeam(e.currentTarget.value)} />
              <PrimaryButton
                disabled={!team || team.length === 0}
                style={buttonStyle}
                onClick={assignTeam}
              >Assign Team</PrimaryButton>
            </Box>
            <Box display="flex" flex={1} justifyContent="end" p={2} style={{ height: '72px'}}>
              <PrimaryButton
                style={buttonStyle}
                onClick={handleOpenModal}
              >Conclude</PrimaryButton>

              <Modal
                  aria-labelledby="transition-modal-title"
                  aria-describedby="transition-modal-description"
                  open={openModal}
                  onClose={handleCloseModal}
                  closeAfterTransition
                >
                  <Fade in={openModal}>
                    <Box css={modalStyle}>
                      <ConcludePlayerComponent
                        sendNotifications={sendNotifications}
                        setSendNotifications={setSendNotifications}
                        concludePlayers={concludePlayers}
                        setConcludeEmailTemplate={setConcludeEmailTemplate}
                        concludeEmailTemplate={concludeEmailTemplate}
                        concludeEmailTemplateOptions={concludeEmailTemplateOptions}
                        closeModal={() => handleCloseModal()}
                      />
                    </Box>
                  </Fade>
                </Modal>
            </Box>
          </Box>
        )}
      />
    </>
  );
};

